<template>
  <sub-page
    v-if="!hideSalaryView"
    title="Salary report"
    subtitle="A breakdown of salaries paid and the payment method used."
  >
    <div class="salary-report">
      <page-list-header>
        <template #left>
          <filter-dropdown v-model="filters" :filters="filterItems" />
        </template>
        <template #right>
          <b-form-group label-cols="auto" label="Per page" label-for="salary-report-per-page" class="mb-0">
            <b-form-select id="salary-report-per-page" v-model="perPage" :options="SALARY_REPORT_PER_PAGE_OPTIONS" />
          </b-form-group>
          <export-button
            :export-data="filteredRows"
            :export-file-rows="exportFileRows"
            :export-headers="exportHeaders"
            file-name="salary-report"
          />
        </template>
      </page-list-header>

      <div class="mb-3">
        <app-loading :loading="loading" />

        <b-table
          v-if="!loading"
          :items="filteredRows"
          :fields="SALARY_REPORT_FIELD_DEFINITIONS"
          :current-page="currentPage"
          :per-page="perPage"
          class="mb-0"
          table-class="table-minimal"
          :tfoot-class="!filteredRows?.length ? 'd-none' : ''"
          primary-key="salary_id"
          foot-clone
          no-footer-sorting
          responsive
          show-empty
          empty-text="No salaries found for the selected dates."
        >
          <template #cell(full_name)="data">
            <router-link :to="{ name: 'employee', params: { employeeId: data.item.employee_id } }">
              {{ data.item.full_name }}
            </router-link>
          </template>
          <template #foot()>&nbsp;</template>
          <template #foot(salary_amount)>{{ totalSalaryAmount }}</template>
        </b-table>
      </div>

      <b-pagination
        v-model="currentPage"
        :total-rows="filteredRows.length"
        :per-page="perPage"
        class="justify-content-end"
      />
    </div>
  </sub-page>
</template>

<script setup>
import { startOfMonth, format } from 'date-fns';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import pick from 'lodash/pick';
import sumBy from 'lodash/sumBy';
import { useQuery } from '@tanstack/vue-query';
import { computed, ref, watch } from 'vue';
import { CONTROL_TYPES } from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import ExportButton from '@/components/ExportButton.vue';
import FilterDropdown from '@/components/FilterDropdown.vue';
import PageListHeader from '@/components/PageListHeader.vue';
import SubPage from '@/components/SubPage.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import { SALARY_REPORT_FIELD_DEFINITIONS, SALARY_REPORT_PER_PAGE_OPTIONS } from '@/pages/reporting/constants';
import { formatPaymentMethod } from '@/pages/reporting/paymentMethod';
import { getSalaryReport } from '@/queries/salary-report';
import { toCurrency } from '@/utils';

const FILTER_KEYS = { STARTS_ON: 'starts_on', ENDS_ON: 'ends_on', PAYMENT_METHOD: 'payment_method' };
const API_FILTER_KEYS = [FILTER_KEYS.STARTS_ON, FILTER_KEYS.ENDS_ON];
const STARTS_ON_DEFAULT = format(startOfMonth(new Date()), 'yyyy-MM-dd');
const ENDS_ON_DEFAULT = format(new Date(), 'yyyy-MM-dd');

const { hideSalaryView } = useFeatureFlags();

const currentPage = ref(1);
const perPage = ref(SALARY_REPORT_PER_PAGE_OPTIONS[0]);
const filters = ref({ [FILTER_KEYS.STARTS_ON]: STARTS_ON_DEFAULT, [FILTER_KEYS.ENDS_ON]: ENDS_ON_DEFAULT });

const appliedApiFilters = computed(() => omitBy(pick(filters.value, API_FILTER_KEYS), value => isNil(value)));

// Endpoint 403s server-side for hidden-salary users; `enabled` keeps it from firing.
const salaryQuery = useQuery({
  ...getSalaryReport(appliedApiFilters),
  enabled: computed(() => !hideSalaryView.value),
});

const rows = computed(() => salaryQuery.data.value ?? []);
const loading = computed(() => salaryQuery.isFetching.value);

const selectedMethods = computed(() => filters.value[FILTER_KEYS.PAYMENT_METHOD] || []);
const availablePaymentMethods = computed(() => [...new Set(rows.value.map(formatPaymentMethod))]);

const filteredRows = computed(() =>
  rows.value.filter(row => !selectedMethods.value.length || selectedMethods.value.includes(formatPaymentMethod(row))),
);

const filterItems = computed(() => [
  {
    key: FILTER_KEYS.STARTS_ON,
    label: 'From',
    type: CONTROL_TYPES.DATE,
    defaultValue: STARTS_ON_DEFAULT,
    controlProps: { max: filters.value[FILTER_KEYS.ENDS_ON] || null },
  },
  {
    key: FILTER_KEYS.ENDS_ON,
    label: 'To',
    type: CONTROL_TYPES.DATE,
    defaultValue: ENDS_ON_DEFAULT,
    controlProps: { min: filters.value[FILTER_KEYS.STARTS_ON] || null },
  },
  {
    key: FILTER_KEYS.PAYMENT_METHOD,
    label: 'Payment method',
    type: CONTROL_TYPES.CHECKBOX,
    options: availablePaymentMethods.value.map(method => ({ text: method, value: method })),
    hidden: availablePaymentMethods.value.length < 2,
  },
]);

const totalSalaryAmount = computed(() => toCurrency(sumBy(filteredRows.value, row => row.salary_amount || 0)));

const exportHeaders = computed(() => SALARY_REPORT_FIELD_DEFINITIONS.map(field => field.label));

const exportFileRows = row =>
  SALARY_REPORT_FIELD_DEFINITIONS.map(field =>
    field.formatter ? field.formatter(row[field.key], field.key, row) : row[field.key],
  );

// Reset to the first page whenever the result set changes (filter/date change).
watch(filteredRows, () => {
  currentPage.value = 1;
});
</script>
